import { ref, computed } from 'vue';
import Request from '@/services/request';
import { SELLER_CATALOG_SEARCH } from "@/utils/enum/endpoints";
import { isValidGtin, isURL, extractGroupId, isAlphaOrAlphaNumeric } from '@/utils/searchFieldValidate';

const Req = new Request();

const DEFAULT_PAGE_COUNT = -1;
const DEFAULT_RESULT_COUNT = -1;

export default function useService(pagination, searchText, filterData) {
  const data = ref(null);
  const filtersItems = ref(null)
  const totalResultCount = ref(DEFAULT_RESULT_COUNT);
  const error = ref(null);
  const isLoading = ref(false);
  const filtersUpdating = ref(true);
  const request = ref(null);
  const pageCount = ref(0);
  const searchValueSuccess = ref(false);
  const searchTextForTemplate = ref(null);
  const didNotFind = ref(null);
  const failedSearchValue = ref(false);

  const fetchSearchData = async (sendFilterParams) => {
    const payload = determinePayload(searchText.value);
    if (!payload) {
      return;
    }
    const requestParams = computed(() => {
      const { pageSize, pageIndex } = pagination.value;
      const currentPage = pageIndex;
      const params = {
        page: currentPage.toString(),
        size: pageSize.toString(),
      };

      if (
        sendFilterParams &&
        (
          filterData.value.filters.brands.length > 0 ||
          filterData.value.filters.categories.length > 0
        )
      ) {
        params.filters = {
          brands: filterData.value.filters.brands.map(i => i.replace(/\s\(\d+\)$/, '')),
          categories: filterData.value.filters.categories.map(i => i.replace(/\s\(\d+\)$/, '')),
        };
      }
      return params;
    });
    isLoading.value = true;
    filtersUpdating.value = !requestParams.value.hasOwnProperty('filters');

    request.value = Req.post(
      SELLER_CATALOG_SEARCH,
      {
        ...payload,
        ...requestParams.value,
      }
    )
      .then(async (response) => {
        if (response.status === 200 && response.data) {
          data.value = response.data.content || [];
          searchValueSuccess.value = true;
          searchTextForTemplate.value = searchText.value;
          error.value = null;
          totalResultCount.value = computed(() => {
            return new Intl.NumberFormat('tr-TR').format(response.data.totalElements);
          }) ?? DEFAULT_PAGE_COUNT;
          didNotFind.value = data.value.length === 0;
          pageCount.value = response.data.totalPages;

          if (filtersUpdating.value && response.data.filters) {
            updateFilterItems(response.data.filters);
          }
        } else {
          throw new Error('Network response was not OK');
        }
      })
      .catch((err) => {
        error.value = err;
        data.value = null;
        totalResultCount.value = DEFAULT_PAGE_COUNT;
        searchValueSuccess.value = false;
      })
      .finally(() => {
        isLoading.value = false;
      });
  };

  const determinePayload = (searchText) => {
    const isPimsId = /^[0-9]{5,10}$/.test(searchText);

    if (isValidGtin(searchText)) {
      failedSearchValue.value = false;
      return {
        productTitle: null,
        productGroupIds: null,
        pimsIds: null,
        uscs: [searchText],
      };
    } else if (isURL(searchText)) {
      failedSearchValue.value = false;
      return {
        productTitle: null,
        productGroupIds: [extractGroupId(searchText)],
        pimsIds: null,
        uscs: null,
      };
    } else if (isPimsId) {
      failedSearchValue.value = false;
      return {
        productTitle: null,
        productGroupIds: null,
        pimsIds: [searchText],
        uscs: null,
      };
    } else if (isAlphaOrAlphaNumeric(searchText)) {
      failedSearchValue.value = false;
      return {
        productTitle: searchText,
        productGroupIds: null,
        pimsIds: null,
        uscs: null,
      };
    } else {
      failedSearchValue.value = true;
      didNotFind.value = false;
      return null;
    }
  };

  function updateFilterItems(filters) {
    const updatedFilterItems = { ...filtersItems.value };

    if (filters.categories) {
      updatedFilterItems.categories = {
        title: 'Kategoriler',
        items: filters.categories.map((item, index) => ({
          id: String(index + 1),
          label: item,
        })),
      };
    }

    if (filters.brands) {
      updatedFilterItems.brands = {
        title: 'Markalar',
        items: filters.brands.map((item, index) => ({
          id: String(index + 1),
          label: item,
        })),
      };
    }
    filtersItems.value = updatedFilterItems;
  }

  return {
    data,
    totalResultCount,
    pageCount,
    error,
    isLoading,
    determinePayload,
    fetchSearchData,
    isValidGtin,
    isURL,
    extractGroupId,
    isAlphaOrAlphaNumeric,
    searchValueSuccess,
    searchTextForTemplate,
    didNotFind,
    failedSearchValue,
    filtersItems,
    updateFilterItems,
    filtersUpdating
  };
}
